 
import React, { CSSProperties, ReactNode, useState } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import { useTailwindBrand } from "../../../Components/shared";
import { DV2, withAlpha } from "../theme";

/**
 * Briques visuelles du nouveau parcours de déclaration (cartes, boutons,
 * badges, encarts), fidèles aux maquettes et communes aux trois marques.
 */

export const useDv2Theme = () => {
    const { brandConfig } = useTailwindBrand();
    return {
        primary: brandConfig.colors.primary,
        text: brandConfig.colors.text,
        primaryDark: brandConfig.colors.dark,
        fontFamily: brandConfig.fontFamily,
        brandName: brandConfig.displayName,
    };
};

interface CardProps {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
    tint?: boolean;
    padding?: number | string;
    as?: "div" | "section" | "aside";
    id?: string;
}

export const Card = ({ children, className = "", style, tint = false, padding = 20, as: Tag = "div", id }: CardProps) => (
    <Tag
        id={id}
        className={className}
        style={{
            background: tint ? DV2.tint : DV2.surface,
            border: tint ? "none" : `1px solid ${DV2.border}`,
            borderRadius: DV2.radius,
            padding,
            ...style,
        }}
    >
        {children}
    </Tag>
);

interface ButtonProps {
    label: ReactNode;
    onClick?: () => void;
    variant?: "primary" | "secondary" | "ghost" | "danger";
    disabled?: boolean;
    loading?: boolean;
    fullWidth?: boolean;
    size?: "md" | "sm";
    type?: "button" | "submit";
    ariaLabel?: string;
    className?: string;
    icon?: ReactNode;
}

export const Button = ({ label, onClick, variant = "primary", disabled, loading, fullWidth, size = "md", type = "button", ariaLabel, className = "", icon }: ButtonProps) => {
    const { primary, fontFamily } = useDv2Theme();
    const isDisabled = disabled || loading;
    const base: CSSProperties = {
        fontFamily,
        fontWeight: 800,
        fontSize: size === "sm" ? 13 : 15,
        padding: size === "sm" ? "9px 16px" : "13px 26px",
        borderRadius: 14,
        border: "1px solid transparent",
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.55 : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        width: fullWidth ? "100%" : undefined,
        transition: "background-color .15s, color .15s, box-shadow .15s",
        whiteSpace: "nowrap",
    };
    const variants: Record<string, CSSProperties> = {
        primary: { background: primary, color: "#fff" },
        secondary: { background: "#fff", color: primary, borderColor: DV2.border },
        ghost: { background: "transparent", color: primary, padding: size === "sm" ? "9px 10px" : "13px 14px" },
        danger: { background: DV2.dangerBg, color: DV2.danger },
    };
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={isDisabled}
            aria-label={ariaLabel}
            aria-busy={loading || undefined}
            className={className}
            style={{ ...base, ...variants[variant] }}
        >
            {loading ? <CircularProgress size={16} color="inherit" /> : icon}
            {label}
        </button>
    );
};

export const Badge = ({ children, tone = "primary" }: { children: ReactNode; tone?: "primary" | "success" | "warning" | "danger" | "muted" }) => {
    const { primary, fontFamily } = useDv2Theme();
    const tones: Record<string, CSSProperties> = {
        primary: { background: DV2.tint, color: primary },
        success: { background: DV2.successBg, color: DV2.success },
        warning: { background: DV2.warningBg, color: DV2.warning },
        danger: { background: DV2.dangerBg, color: DV2.danger },
        muted: { background: "#F1F4F8", color: DV2.muted },
    };
    return (
        <span style={{ fontFamily, fontSize: 12, fontWeight: 800, padding: "4px 10px", borderRadius: 999, display: "inline-block", ...tones[tone] }}>
            {children}
        </span>
    );
};

/** Encart d'alerte : erreur liée au champ, avertissement, information. */
export const Notice = ({ children, tone = "info", role, id, action }: { children: ReactNode; tone?: "info" | "warning" | "danger" | "success"; role?: string; id?: string; action?: ReactNode }) => {
    const { primary, fontFamily } = useDv2Theme();
    const tones: Record<string, { bg: string; color: string; border: string }> = {
        info: { bg: DV2.tint, color: DV2.textSoft, border: withAlpha(primary, 0.25) },
        warning: { bg: DV2.warningBg, color: DV2.warning, border: "#F3C98B" },
        danger: { bg: DV2.dangerBg, color: DV2.danger, border: "#F0B5B0" },
        success: { bg: DV2.successBg, color: DV2.success, border: "#BFE3CB" },
    };
    const t = tones[tone];
    return (
        <div
            id={id}
            role={role ?? (tone === "danger" ? "alert" : undefined)}
            style={{ fontFamily, background: t.bg, color: t.color, border: `1px solid ${t.border}`, borderRadius: DV2.radiusSm, padding: "10px 14px", fontSize: 13, lineHeight: 1.5, display: "flex", gap: 10, alignItems: "flex-start" }}
        >
            <span aria-hidden style={{ fontWeight: 800, flexShrink: 0 }}>{tone === "danger" || tone === "warning" ? "!" : tone === "success" ? "✓" : "i"}</span>
            <div style={{ flex: 1 }}>{children}</div>
            {action}
        </div>
    );
};

/** Avatar à initiales. */
export const Avatar = ({ text, size = 40 }: { text: string; size?: number }) => {
    const { primary, fontFamily } = useDv2Theme();
    return (
        <div aria-hidden style={{ width: size, height: size, borderRadius: "50%", background: DV2.tint, color: primary, fontFamily, fontWeight: 800, fontSize: size * 0.36, display: "grid", placeItems: "center", flexShrink: 0 }}>
            {text}
        </div>
    );
};

/** Ligne « libellé … valeur » des encarts et récapitulatifs. */
export const KeyValue = ({ label, value, strong = true }: { label: ReactNode; value: ReactNode; strong?: boolean }) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 }}>
        <span style={{ color: DV2.textSoft }}>{label}</span>
        <span style={{ fontWeight: strong ? 800 : 500, textAlign: "right" }}>{value}</span>
    </div>
);

/** Lien texte dans la couleur de la marque. */
export const TextLink = ({ label, onClick, href, size = 13 }: { label: ReactNode; onClick?: () => void; href?: string; size?: number }) => {
    const { primary, fontFamily } = useDv2Theme();
    const style: CSSProperties = { color: primary, fontFamily, fontWeight: 800, fontSize: size, background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "none" };
    if (href) {
        return <a href={href} target="_blank" rel="noreferrer" style={style}>{label}</a>;
    }
    return <button type="button" onClick={onClick} style={style}>{label}</button>;
};

/**
 * Petit « i » d'information : l'explication s'affiche au survol, au focus et au
 * clic ou au toucher (sur mobile il n'y a pas de survol).
 */
export const InfoTip = ({ text, label = "Plus d'informations" }: { text: ReactNode; label?: string }) => {
    const { primary, fontFamily } = useDv2Theme();
    const [open, setOpen] = useState(false);
    return (
        <Tooltip
            title={<span style={{ fontFamily, fontSize: 13, lineHeight: 1.45 }}>{text}</span>}
            arrow
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            disableTouchListener
            enterTouchDelay={0}
        >
            <button
                type="button"
                aria-label={label}
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                onBlur={() => setOpen(false)}
                style={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${primary}`, background: "#fff", color: primary, fontFamily, fontSize: 11, fontWeight: 800, lineHeight: 1, display: "inline-grid", placeItems: "center", cursor: "pointer", padding: 0, verticalAlign: "middle", textTransform: "none", letterSpacing: 0 }}
            >
                i
            </button>
        </Tooltip>
    );
};

/** Titre de section dans une carte. */
export const SectionTitle = ({ children, right }: { children: ReactNode; right?: ReactNode }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 15 }}>{children}</div>
        {right}
    </div>
);

/** Libellé de champ. */
export const FieldLabel = ({ children, htmlFor, hint }: { children: ReactNode; htmlFor?: string; hint?: ReactNode }) => (
    <label htmlFor={htmlFor} style={{ display: "block", fontSize: 13, fontWeight: 800, color: DV2.textSoft, marginBottom: 6 }}>
        {children}
        {hint && <span style={{ fontWeight: 500, color: DV2.placeholder }}> {hint}</span>}
    </label>
);

/** Style commun des champs texte du parcours. */
export const fieldStyle = (error?: boolean): CSSProperties => ({
    width: "100%",
    // un champ date sur mobile a une largeur intrinsèque : on la borne à sa colonne
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    background: "#fff",
    border: `1px solid ${error ? DV2.danger : DV2.border}`,
    borderRadius: DV2.radiusSm,
    padding: "11px 14px",
    fontSize: 15,
    fontWeight: 700,
    color: DV2.text,
    outline: "none",
});

export const FieldError = ({ id, children }: { id?: string; children: ReactNode }) => (
    <p id={id} role="alert" style={{ color: DV2.danger, fontSize: 12, fontWeight: 700, margin: "6px 0 0" }}>{children}</p>
);

export const Hint = ({ children }: { children: ReactNode }) => (
    <p style={{ color: DV2.placeholder, fontSize: 12, margin: "6px 0 0", lineHeight: 1.45 }}>{children}</p>
);
